import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Card, Select, Button, Tag, Modal, Input, Alert, Spin, message, Popconfirm } from 'antd';
import { CheckCircleFilled, WarningFilled, LockOutlined, UnlockOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { toJalali } from '../../utils/dateUtils';

const { Title, Text } = Typography;

const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const toFa = (v) => String(v).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const MonthlyChecklist = () => {
  const navigate = useNavigate();
  const now = moment();
  const currentYear = now.jYear();
  const currentMonth = now.jMonth() + 1;
  const [period, setPeriod] = useState({ year: currentYear, month: currentMonth });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financialApi.getMonthlyChecklist(period.year, period.month);
      setData(res?.data || res);
    } catch (err) {
      setData(null);
      message.error(extractApiUserMessage(err) || 'خطا در دریافت چک‌لیست');
    } finally {
      setLoading(false);
    }
  }, [period.year, period.month]);

  useEffect(() => { load(); }, [load]);

  const isClosed = data?.closing?.status === 'closed';
  const warnings = data?.summary?.warning_count || 0;
  const monthName = `${JALALI_MONTHS[period.month - 1]} ${toFa(period.year)}`;
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => ({ value: y, label: toFa(y) }));

  const doClose = async () => {
    setSaving(true);
    try {
      const res = await financialApi.closeMonthlyChecklist(period.year, period.month, note.trim());
      setData(res?.data || res);
      message.success(`گزارش ${monthName} بسته شد.`);
      setCloseOpen(false);
      setNote('');
    } catch (err) {
      message.error(extractApiUserMessage(err) || 'خطا در بستن گزارش');
    } finally {
      setSaving(false);
    }
  };

  const doReopen = async () => {
    setSaving(true);
    try {
      const res = await financialApi.reopenMonthlyChecklist(period.year, period.month);
      setData(res?.data || res);
      message.success('ماه بازگشایی شد.');
    } catch (err) {
      message.error(extractApiUserMessage(err) || 'خطا در بازگشایی ماه');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-3xl mx-auto" style={{ fontFamily: 'CustomFont' }}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Title level={3} className="m-0" style={{ fontFamily: 'CustomFont' }}>چک‌لیست ماهانه مالی</Title>
            <Text type="secondary">بررسی مدیریتی پایان ماه؛ بستن گزارش به معنی بستن حسابداری رسمی نیست.</Text>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={period.month}
              onChange={(m) => setPeriod((p) => ({ ...p, month: m }))}
              options={JALALI_MONTHS.map((name, i) => ({ value: i + 1, label: name }))}
              style={{ width: 130, fontFamily: 'CustomFont' }}
            />
            <Select
              value={period.year}
              onChange={(y) => setPeriod((p) => ({ ...p, year: y }))}
              options={yearOptions}
              style={{ width: 100, fontFamily: 'CustomFont' }}
            />
          </div>
        </div>

        <Spin spinning={loading}>
          <Card className="shadow-sm border-0 rounded-2xl">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="text-lg font-bold text-gray-800 dark:text-gray-100">وضعیت مالی {monthName}</div>
              {data && (
                <div className="flex items-center gap-2">
                  <Tag color={warnings ? 'orange' : 'green'} style={{ margin: 0 }}>
                    {warnings ? `${toFa(warnings)} مورد نیازمند بررسی` : 'همه‌ی موارد مرتب است'}
                  </Tag>
                  {isClosed && <Tag color="blue" icon={<LockOutlined />} style={{ margin: 0 }}>بسته‌شده</Tag>}
                </div>
              )}
            </div>

            {data?.items?.map((item) => (
              <div
                key={item.key}
                onClick={item.status === 'warning' && item.link ? () => navigate(item.link) : undefined}
                className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 mb-2
                  ${item.status === 'warning'
                    ? 'border-orange-200 bg-orange-50/60 dark:bg-orange-900/10 dark:border-orange-900/40 cursor-pointer hover:bg-orange-50'
                    : 'border-gray-100 dark:border-gray-700'}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {item.status === 'ok'
                    ? <CheckCircleFilled style={{ color: '#16a34a', fontSize: 18 }} />
                    : <WarningFilled style={{ color: '#f59e0b', fontSize: 18 }} />}
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{toFa(item.text)}</div>
                  </div>
                </div>
                {item.status === 'warning' && <RightOutlined className="text-gray-400" style={{ transform: 'scaleX(-1)' }} />}
              </div>
            ))}

            {data?.closing && (
              <Alert
                className="mt-3"
                type={isClosed ? 'info' : 'warning'}
                showIcon
                message={isClosed
                  ? `این ماه ${data.closing.closed_by ? `توسط ${data.closing.closed_by} ` : ''}در ${toJalali(data.closing.closed_at) || ''} بسته شده است.`
                  : 'این ماه قبلاً بسته و سپس بازگشایی شده است.'}
                description={isClosed && data.closing.note ? `توضیح: ${data.closing.note}` : undefined}
              />
            )}

            {data && (
              <div className="mt-4 flex flex-wrap gap-2">
                {isClosed ? (
                  <Popconfirm title="ماه بازگشایی شود؟" okText="بله" cancelText="خیر" onConfirm={doReopen}>
                    <Button icon={<UnlockOutlined />} loading={saving} style={{ fontFamily: 'CustomFont' }}>بازگشایی ماه</Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" icon={<LockOutlined />} onClick={() => setCloseOpen(true)} style={{ fontFamily: 'CustomFont' }}>
                    بستن گزارش {JALALI_MONTHS[period.month - 1]}
                  </Button>
                )}
                <Button onClick={load} style={{ fontFamily: 'CustomFont' }}>به‌روزرسانی</Button>
              </div>
            )}
          </Card>
        </Spin>

        <Modal
          open={closeOpen}
          onCancel={() => setCloseOpen(false)}
          onOk={doClose}
          confirmLoading={saving}
          okText="بستن گزارش"
          cancelText="انصراف"
          okButtonProps={{ disabled: warnings > 0 && !note.trim() }}
          title={<span style={{ fontFamily: 'CustomFont' }}>بستن گزارش {monthName}</span>}
        >
          <div style={{ fontFamily: 'CustomFont' }}>
            {warnings > 0 ? (
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                message={`${toFa(warnings)} مورد هنوز نیازمند بررسی است.`}
                description="برای بستن با وجود هشدارها، دلیل را بنویسید."
              />
            ) : (
              <div className="mb-3 text-sm text-gray-600">همه‌ی موارد چک‌لیست مرتب است.</div>
            )}
            <Input.TextArea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={warnings > 0 ? 'توضیح (الزامی)' : 'توضیح (اختیاری)'}
            />
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default MonthlyChecklist;
